import BaseService from '@/services/BaseService';
import { dedupeExposureFacilityOptions } from './utils/dedupeExposureFacilityOptions';
import { sortExposureFacilityOptions } from './utils/sortExposureFacilityOptions';
import { internalRatingDirectionMeta } from './utils/internalRatingMigration';

export type EnergyExposureKpis = {
  grossUsedAmountUsd: number;
  netUsedAmountUsd: number;
  participationUsedAmountUsd: number;
  totalCommittedAmountUsd: number;
  wtdAvgMarginPlusCas: number | null;
};

const ENERGY_KPIS_PATH = 'exposure/energy/kpis';
const ENERGY_AS_OF_DATES_PATH = 'exposure/energy/as-of-dates';
const TRANSPORTATION_KPIS_PATH = 'exposure/transportation/kpis';
const TRANSPORTATION_AS_OF_DATES_PATH = 'exposure/transportation/as-of-dates';
const TRANSPORTATION_DAILY_PORTFOLIO_BALANCE_PATH = 'exposure/transportation/daily-portfolio-balance-series';
const TRANSPORTATION_QUARTERLY_FUNDED_BALANCE_PATH = 'exposure/transportation/quarterly-funded-balance-series';
const TRANSPORTATION_FACILITIES_PATH = 'exposure/transportation/facilities';
const TRANSPORTATION_FACILITY_UTILIZATION_PATH = 'exposure/transportation/facility-utilization-series';
const TRANSPORTATION_WEIGHTED_RATE_TREND_PATH = 'exposure/transportation/weighted-rate-trend-series';
const TRANSPORTATION_FACILITY_CONCENTRATION_TOP10_PATH = 'exposure/transportation/facility-concentration-top10';
const TRANSPORTATION_RISK_LIMIT_STRATIFICATION_PATH = 'exposure/transportation/risk-limit-stratification';
const TRANSPORTATION_CONCENTRATION_METRICS_PATH = 'exposure/transportation/concentration-metrics';
const TRANSPORTATION_TOP_COUNTERPARTIES_PATH = 'exposure/transportation/top-counterparties';
const AUTO_KPIS_PATH = 'exposure/auto/kpis';
const AUTO_AS_OF_DATES_PATH = 'exposure/auto/as-of-dates';
const AUTO_DAILY_PORTFOLIO_BALANCE_PATH = 'exposure/auto/daily-portfolio-balance-series';
const AUTO_QUARTERLY_FUNDED_BALANCE_PATH = 'exposure/auto/quarterly-funded-balance-series';
const AUTO_FACILITIES_PATH = 'exposure/auto/facilities';
const AUTO_FACILITY_UTILIZATION_PATH = 'exposure/auto/facility-utilization-series';
const AUTO_WEIGHTED_RATE_TREND_PATH = 'exposure/auto/weighted-rate-trend-series';
const AUTO_FACILITY_CONCENTRATION_TOP10_PATH = 'exposure/auto/facility-concentration-top10';
const AUTO_RISK_LIMIT_STRATIFICATION_PATH = 'exposure/auto/risk-limit-stratification';
const AUTO_CONCENTRATION_METRICS_PATH = 'exposure/auto/concentration-metrics';
const AUTO_TOP_COUNTERPARTIES_PATH = 'exposure/auto/top-counterparties';
const APAC_KPIS_PATH = 'exposure/apac/kpis';
const APAC_AS_OF_DATES_PATH = 'exposure/apac/as-of-dates';
const APAC_DAILY_PORTFOLIO_BALANCE_PATH = 'exposure/apac/daily-portfolio-balance-series';
const APAC_QUARTERLY_FUNDED_BALANCE_PATH = 'exposure/apac/quarterly-funded-balance-series';
const APAC_FACILITIES_PATH = 'exposure/apac/facilities';
const APAC_FACILITY_UTILIZATION_PATH = 'exposure/apac/facility-utilization-series';
const APAC_WEIGHTED_RATE_TREND_PATH = 'exposure/apac/weighted-rate-trend-series';
const APAC_FACILITY_CONCENTRATION_TOP10_PATH = 'exposure/apac/facility-concentration-top10';
const APAC_RISK_LIMIT_STRATIFICATION_PATH = 'exposure/apac/risk-limit-stratification';
const APAC_CONCENTRATION_METRICS_PATH = 'exposure/apac/concentration-metrics';
const APAC_TOP_COUNTERPARTIES_PATH = 'exposure/apac/top-counterparties';
const EMEA_KPIS_PATH = 'exposure/emea/kpis';
const EMEA_AS_OF_DATES_PATH = 'exposure/emea/as-of-dates';
const EMEA_DAILY_PORTFOLIO_BALANCE_PATH = 'exposure/emea/daily-portfolio-balance-series';
const EMEA_QUARTERLY_FUNDED_BALANCE_PATH = 'exposure/emea/quarterly-funded-balance-series';
const EMEA_FACILITIES_PATH = 'exposure/emea/facilities';
const EMEA_FACILITY_UTILIZATION_PATH = 'exposure/emea/facility-utilization-series';
const EMEA_WEIGHTED_RATE_TREND_PATH = 'exposure/emea/weighted-rate-trend-series';
const EMEA_FACILITY_CONCENTRATION_TOP10_PATH = 'exposure/emea/facility-concentration-top10';
const EMEA_RISK_LIMIT_STRATIFICATION_PATH = 'exposure/emea/risk-limit-stratification';
const EMEA_CONCENTRATION_METRICS_PATH = 'exposure/emea/concentration-metrics';
const EMEA_TOP_COUNTERPARTIES_PATH = 'exposure/emea/top-counterparties';
const MARKETPLACE_KPIS_PATH = 'exposure/marketplace/kpis';
const MARKETPLACE_AS_OF_DATES_PATH = 'exposure/marketplace/as-of-dates';
const MARKETPLACE_DAILY_PORTFOLIO_BALANCE_PATH = 'exposure/marketplace/daily-portfolio-balance-series';
const MARKETPLACE_QUARTERLY_FUNDED_BALANCE_PATH = 'exposure/marketplace/quarterly-funded-balance-series';
const MARKETPLACE_FACILITIES_PATH = 'exposure/marketplace/facilities';
const MARKETPLACE_FACILITY_UTILIZATION_PATH = 'exposure/marketplace/facility-utilization-series';
const MARKETPLACE_WEIGHTED_RATE_TREND_PATH = 'exposure/marketplace/weighted-rate-trend-series';
const MARKETPLACE_FACILITY_CONCENTRATION_TOP10_PATH = 'exposure/marketplace/facility-concentration-top10';
const MARKETPLACE_RISK_LIMIT_STRATIFICATION_PATH = 'exposure/marketplace/risk-limit-stratification';
const MARKETPLACE_CONCENTRATION_METRICS_PATH = 'exposure/marketplace/concentration-metrics';
const MARKETPLACE_TOP_COUNTERPARTIES_PATH = 'exposure/marketplace/top-counterparties';
const FUND_FINANCE_KPIS_PATH = 'exposure/fund-finance/kpis';
const FUND_FINANCE_AS_OF_DATES_PATH = 'exposure/fund-finance/as-of-dates';
const FUND_FINANCE_DAILY_PORTFOLIO_BALANCE_PATH = 'exposure/fund-finance/daily-portfolio-balance-series';
const FUND_FINANCE_QUARTERLY_FUNDED_BALANCE_PATH = 'exposure/fund-finance/quarterly-funded-balance-series';
const FUND_FINANCE_FACILITIES_PATH = 'exposure/fund-finance/facilities';
const FUND_FINANCE_FACILITY_UTILIZATION_PATH = 'exposure/fund-finance/facility-utilization-series';
const FUND_FINANCE_WEIGHTED_RATE_TREND_PATH = 'exposure/fund-finance/weighted-rate-trend-series';
const FUND_FINANCE_FACILITY_CONCENTRATION_TOP10_PATH = 'exposure/fund-finance/facility-concentration-top10';
const FUND_FINANCE_RISK_LIMIT_STRATIFICATION_PATH = 'exposure/fund-finance/risk-limit-stratification';
const FUND_FINANCE_CONCENTRATION_METRICS_PATH = 'exposure/fund-finance/concentration-metrics';
const FUND_FINANCE_TOP_COUNTERPARTIES_PATH = 'exposure/fund-finance/top-counterparties';
const ENERGY_DAILY_PORTFOLIO_BALANCE_PATH = 'exposure/energy/daily-portfolio-balance-series';
const ENERGY_FACILITY_UTILIZATION_PATH = 'exposure/energy/facility-utilization-series';
const ENERGY_WEIGHTED_RATE_TREND_PATH = 'exposure/energy/weighted-rate-trend-series';
const ENERGY_QUARTERLY_FUNDED_BALANCE_PATH = 'exposure/energy/quarterly-funded-balance-series';
const ENERGY_FACILITIES_PATH = 'exposure/energy/facilities';
const ENERGY_FACILITY_CONCENTRATION_TOP10_PATH = 'exposure/energy/facility-concentration-top10';
const ENERGY_RISK_LIMIT_STRATIFICATION_PATH = 'exposure/energy/risk-limit-stratification';
const ENERGY_CONCENTRATION_METRICS_PATH = 'exposure/energy/concentration-metrics';
const ENERGY_TOP_COUNTERPARTIES_PATH = 'exposure/energy/top-counterparties';
const ENERGY_RISK_LIMIT_CONCENTRATION_PATH = 'exposure/energy/risk-limit-concentration-series';
const TRANSPORTATION_RISK_LIMIT_CONCENTRATION_PATH = 'exposure/transportation/risk-limit-concentration-series';
const AUTO_RISK_LIMIT_CONCENTRATION_PATH = 'exposure/auto/risk-limit-concentration-series';
const APAC_RISK_LIMIT_CONCENTRATION_PATH = 'exposure/apac/risk-limit-concentration-series';
const EMEA_RISK_LIMIT_CONCENTRATION_PATH = 'exposure/emea/risk-limit-concentration-series';
const MARKETPLACE_RISK_LIMIT_CONCENTRATION_PATH = 'exposure/marketplace/risk-limit-concentration-series';
const FUND_FINANCE_RISK_LIMIT_CONCENTRATION_PATH = 'exposure/fund-finance/risk-limit-concentration-series';

/** Aligns chart/strat labels with backend Energy bucket rules (Infrastructure / Not Applicable never shown as raw CLS values). */
function displayEnergyRiskLimitBucket(bucket: string): string {
  const b = bucket.trim();
  if (b === 'Infrastructure' || b === 'Not Applicable') {
    return 'Energy - C&I';
  }
  return b;
}

type RiskLimitConcentrationDisplayMode = 'energy' | 'plain';

function mergeEnergyRiskLimitConcentrationRows(
  rows: EnergyRiskLimitConcentrationRow[],
  displayMode: RiskLimitConcentrationDisplayMode = 'energy',
): EnergyRiskLimitConcentrationRow[] {
  const m = new Map<string, EnergyRiskLimitConcentrationRow>();
  for (const r of rows) {
    const bucket = displayMode === 'plain' ? r.riskLimitBucket.trim() : displayEnergyRiskLimitBucket(r.riskLimitBucket);
    const key = `${r.asOfDate}\t${r.quarter}\t${bucket}`;
    const prev = m.get(key);
    if (prev) {
      prev.fundedUsd += r.fundedUsd;
    } else {
      m.set(key, { ...r, riskLimitBucket: bucket });
    }
  }
  return [...m.values()].sort((a, b) => {
    const d = a.asOfDate.localeCompare(b.asOfDate);
    if (d !== 0) {
      return d;
    }
    return a.quarter.localeCompare(b.quarter);
  });
}
const ENERGY_QTD_TOP_INCREASES_PATH = 'exposure/energy/qtd-top-increases';
const ENERGY_QTD_TOP_DECREASES_PATH = 'exposure/energy/qtd-top-decreases';
const ENERGY_YTD_TOP_INCREASES_PATH = 'exposure/energy/ytd-top-increases';
const ENERGY_YTD_TOP_DECREASES_PATH = 'exposure/energy/ytd-top-decreases';
const TRANSPORTATION_QTD_TOP_INCREASES_PATH = 'exposure/transportation/qtd-top-increases';
const TRANSPORTATION_QTD_TOP_DECREASES_PATH = 'exposure/transportation/qtd-top-decreases';
const TRANSPORTATION_YTD_TOP_INCREASES_PATH = 'exposure/transportation/ytd-top-increases';
const TRANSPORTATION_YTD_TOP_DECREASES_PATH = 'exposure/transportation/ytd-top-decreases';
const ENERGY_LARGE_FUNDINGS_OVER_5MM_PATH = 'exposure/energy/large-fundings-over-5mm';
const ENERGY_LARGE_PAYDOWNS_OVER_5MM_PATH = 'exposure/energy/large-paydowns-over-5mm';
const TRANSPORTATION_LARGE_FUNDINGS_OVER_5MM_PATH = 'exposure/transportation/large-fundings-over-5mm';
const TRANSPORTATION_LARGE_PAYDOWNS_OVER_5MM_PATH = 'exposure/transportation/large-paydowns-over-5mm';
const ENERGY_INTERNAL_RATING_MIGRATION_PATH = 'exposure/energy/internal-rating-migration';
const ENERGY_INTERNAL_RATING_CHANGES_PATH = 'exposure/energy/internal-rating-changes';
const TRANSPORTATION_INTERNAL_RATING_MIGRATION_PATH = 'exposure/transportation/internal-rating-migration';
const TRANSPORTATION_INTERNAL_RATING_CHANGES_PATH = 'exposure/transportation/internal-rating-changes';
const ENERGY_CURRENT_POSITIONS_PATH = 'exposure/energy/current-positions';
const ENERGY_SOLAR_EXPOSURE_CHANGES_SUMMARY_PATH = 'exposure/energy/solar-exposure-changes/summary';
const ENERGY_SOLAR_EXPOSURE_CHANGES_BY_FUNDING_VEHICLE_PATH =
  'exposure/energy/solar-exposure-changes/by-funding-vehicle';
const TRANSPORTATION_CURRENT_POSITIONS_PATH = 'exposure/transportation/current-positions';
const AUTO_QTD_TOP_INCREASES_PATH = 'exposure/auto/qtd-top-increases';
const AUTO_QTD_TOP_DECREASES_PATH = 'exposure/auto/qtd-top-decreases';
const AUTO_YTD_TOP_INCREASES_PATH = 'exposure/auto/ytd-top-increases';
const AUTO_YTD_TOP_DECREASES_PATH = 'exposure/auto/ytd-top-decreases';
const AUTO_LARGE_FUNDINGS_OVER_5MM_PATH = 'exposure/auto/large-fundings-over-5mm';
const AUTO_LARGE_PAYDOWNS_OVER_5MM_PATH = 'exposure/auto/large-paydowns-over-5mm';
const AUTO_INTERNAL_RATING_MIGRATION_PATH = 'exposure/auto/internal-rating-migration';
const AUTO_INTERNAL_RATING_CHANGES_PATH = 'exposure/auto/internal-rating-changes';
const AUTO_CURRENT_POSITIONS_PATH = 'exposure/auto/current-positions';
const APAC_QTD_TOP_INCREASES_PATH = 'exposure/apac/qtd-top-increases';
const APAC_QTD_TOP_DECREASES_PATH = 'exposure/apac/qtd-top-decreases';
const APAC_YTD_TOP_INCREASES_PATH = 'exposure/apac/ytd-top-increases';
const APAC_YTD_TOP_DECREASES_PATH = 'exposure/apac/ytd-top-decreases';
const APAC_LARGE_FUNDINGS_OVER_5MM_PATH = 'exposure/apac/large-fundings-over-5mm';
const APAC_LARGE_PAYDOWNS_OVER_5MM_PATH = 'exposure/apac/large-paydowns-over-5mm';
const APAC_INTERNAL_RATING_MIGRATION_PATH = 'exposure/apac/internal-rating-migration';
const APAC_INTERNAL_RATING_CHANGES_PATH = 'exposure/apac/internal-rating-changes';
const APAC_CURRENT_POSITIONS_PATH = 'exposure/apac/current-positions';
const EMEA_QTD_TOP_INCREASES_PATH = 'exposure/emea/qtd-top-increases';
const EMEA_QTD_TOP_DECREASES_PATH = 'exposure/emea/qtd-top-decreases';
const EMEA_YTD_TOP_INCREASES_PATH = 'exposure/emea/ytd-top-increases';
const EMEA_YTD_TOP_DECREASES_PATH = 'exposure/emea/ytd-top-decreases';
const EMEA_LARGE_FUNDINGS_OVER_5MM_PATH = 'exposure/emea/large-fundings-over-5mm';
const EMEA_LARGE_PAYDOWNS_OVER_5MM_PATH = 'exposure/emea/large-paydowns-over-5mm';
const EMEA_INTERNAL_RATING_MIGRATION_PATH = 'exposure/emea/internal-rating-migration';
const EMEA_INTERNAL_RATING_CHANGES_PATH = 'exposure/emea/internal-rating-changes';
const EMEA_CURRENT_POSITIONS_PATH = 'exposure/emea/current-positions';
const MARKETPLACE_QTD_TOP_INCREASES_PATH = 'exposure/marketplace/qtd-top-increases';
const MARKETPLACE_QTD_TOP_DECREASES_PATH = 'exposure/marketplace/qtd-top-decreases';
const MARKETPLACE_YTD_TOP_INCREASES_PATH = 'exposure/marketplace/ytd-top-increases';
const MARKETPLACE_YTD_TOP_DECREASES_PATH = 'exposure/marketplace/ytd-top-decreases';
const MARKETPLACE_LARGE_FUNDINGS_OVER_5MM_PATH = 'exposure/marketplace/large-fundings-over-5mm';
const MARKETPLACE_LARGE_PAYDOWNS_OVER_5MM_PATH = 'exposure/marketplace/large-paydowns-over-5mm';
const MARKETPLACE_INTERNAL_RATING_MIGRATION_PATH = 'exposure/marketplace/internal-rating-migration';
const MARKETPLACE_INTERNAL_RATING_CHANGES_PATH = 'exposure/marketplace/internal-rating-changes';
const MARKETPLACE_CURRENT_POSITIONS_PATH = 'exposure/marketplace/current-positions';
const FUND_FINANCE_QTD_TOP_INCREASES_PATH = 'exposure/fund-finance/qtd-top-increases';
const FUND_FINANCE_QTD_TOP_DECREASES_PATH = 'exposure/fund-finance/qtd-top-decreases';
const FUND_FINANCE_YTD_TOP_INCREASES_PATH = 'exposure/fund-finance/ytd-top-increases';
const FUND_FINANCE_YTD_TOP_DECREASES_PATH = 'exposure/fund-finance/ytd-top-decreases';
const FUND_FINANCE_LARGE_FUNDINGS_OVER_5MM_PATH = 'exposure/fund-finance/large-fundings-over-5mm';
const FUND_FINANCE_LARGE_PAYDOWNS_OVER_5MM_PATH = 'exposure/fund-finance/large-paydowns-over-5mm';
const FUND_FINANCE_INTERNAL_RATING_MIGRATION_PATH = 'exposure/fund-finance/internal-rating-migration';
const FUND_FINANCE_INTERNAL_RATING_CHANGES_PATH = 'exposure/fund-finance/internal-rating-changes';
const FUND_FINANCE_CURRENT_POSITIONS_PATH = 'exposure/fund-finance/current-positions';

export type StratFinDeskExposureSegmentSlug =
  | 'full-portfolio'
  | 'cons-comm'
  | 'cre'
  | 'private-credit-finance'
  | 'resi'
  | 'tac-sits';

function stratFinDeskExposurePath(segmentSlug: StratFinDeskExposureSegmentSlug, suffix: string): string {
  return `exposure/strat-fin-desk/${segmentSlug}/${suffix}`;
}

export type EnergyDailyPortfolioBalancePoint = {
  asOfDate: string;
  grossUsedAmountUsd: number;
  netUsedAmountUsd: number;
  committedAmountUsd: number;
};

export type EnergyFacilityUtilizationPoint = {
  asOfDate: string;
  utilizationPct: number | null;
};

export type ExposureWeightedRateTrendPoint = {
  asOfDate: string;
  maxAdvanceRateCommittedPct: number | null;
  maxAdvanceRateFundedPct: number | null;
  waUsedMarginCommittedPct: number | null;
  waUsedMarginFundedPct: number | null;
  waYieldRateFundedPct: number | null;
  oneMonthTermSofrPct: number | null;
};

export type EnergyQuarterlyFundedBalancePoint = {
  quarterLabel: string;
  netUsedAmountUsd: number;
  participationUsedAmountUsd: number;
};

export type EnergyQuarterlyFundedBalanceBridgeWalk = {
  newOriginationsUsd: number;
  fundingUsd: number;
  paydownsUsd: number;
  priorQuarterLabel: string;
  currentQuarterLabel: string;
};

export type EnergyQuarterlyFundedBalanceSeriesPayload = {
  quarters: EnergyQuarterlyFundedBalancePoint[];
  latestBridge: EnergyQuarterlyFundedBalanceBridgeWalk | null;
};

export type EnergyExposureFacilityOption = {
  dealName: string;
  csId: string | null;
  displayLabel: string;
  isTerminated: boolean;
};

export type EnergyFacilityConcentrationFacilityRow = {
  borrowerName: string;
  grossUsedAmountUsd: number;
  pctOfPortfolioTotal: number;
};

export type EnergyFacilityConcentrationTop10Response = {
  asOfDate: string;
  portfolioTotalGrossUsedUsd: number;
  top10CombinedSharePct: number;
  facilities: EnergyFacilityConcentrationFacilityRow[];
};

export type EnergyRiskLimitStratificationRow = {
  riskLimit: string;
  funded: number;
  commitment: number;
  qtdChange: number;
  ytdChange: number;
  participation: number;
};

export type FullPortfolioAssetClassSummaryRow = {
  assetClass: string;
  crmApprovedUsd: number;
  committedAmountUsd: number;
  netExposureUsd: number;
  usedAmountUsd: number;
  waMaxAdvanceRateCommitPct: number | null;
  waUsedMarginCommitPct: number | null;
  waUsedMarginFundedPct: number | null;
  waYearsRemainDp: number | null;
};

export type EnergyConcentrationMetrics = {
  asOfDate: string;
  activeFacilities: number;
  largestSingleFacilityUsd: number;
  largestSingleFacilityShare: number;
  top5Share: number;
  top10Share: number;
};

export type EnergyTopCounterpartyRow = {
  clientName: string;
  funded: number;
  fundedPct: number | null;
  netExposure: number;
  netExposurePct: number | null;
  committed: number;
  committedPct: number | null;
  participation: number;
  participationPct: number | null;
};

export type EnergyRiskLimitConcentrationRow = {
  asOfDate: string;
  quarter: string;
  riskLimitBucket: string;
  fundedUsd: number;
};

export type EnergyQtdTopIncreaseRow = {
  dealName: string;
  riskLimitBucket: string;
  usedBalanceUsd: number;
  usedChangeUsd: number;
  committedChangeUsd: number;
};

export type EnergyQtdTopIncreasesPayload = {
  priorPeriodLabel: string;
  rows: EnergyQtdTopIncreaseRow[];
};

/** Same payload shape for QTD/YTD top movers (increases and decreases). */
export type EnergyDealMoversPayload = EnergyQtdTopIncreasesPayload;

export type EnergyLargeMovementRow = {
  asOfDate: string;
  deal: string;
  amount: number;
  committedDelta: number;
  csId?: string | number;
  userTag?: string;
};

export type EnergyInternalRatingMigrationCell = {
  priorRating: string;
  currentRating: string;
  facilityCount: number;
};

export type EnergyInternalRatingChangeRow = {
  borrowerName: string;
  usedUsd: number;
  prior: string;
  current: string;
  direction: string;
  directionTone: 'new' | 'down' | 'up' | 'muted';
};

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

function normalizeDailyPortfolioPoints(raw: unknown): EnergyDailyPortfolioBalancePoint[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map((row): EnergyDailyPortfolioBalancePoint | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const d = r.asOfDate ?? r.AsOfDate;
      const v = r.grossUsedAmountUsd ?? r.GrossUsedAmountUsd;
      const netV = r.netUsedAmountUsd ?? r.NetUsedAmountUsd;
      const c = r.committedAmountUsd ?? r.CommittedAmountUsd;
      if (d == null || v == null) {
        return null;
      }
      const asOfDate = String(d).trim().slice(0, 10);
      const gross = typeof v === 'number' ? v : Number(v);
      const netRaw = netV == null ? gross : typeof netV === 'number' ? netV : Number(netV);
      const committedRaw = typeof c === 'number' ? c : c == null || c === '' ? 0 : Number(c);
      if (!Number.isFinite(gross) || !Number.isFinite(netRaw)) {
        return null;
      }
      return {
        asOfDate,
        grossUsedAmountUsd: gross,
        netUsedAmountUsd: netRaw,
        committedAmountUsd: Number.isFinite(committedRaw) ? committedRaw : 0,
      };
    })
    .filter((x): x is EnergyDailyPortfolioBalancePoint => x != null);
}

function normalizeFacilityUtilizationPoints(raw: unknown): EnergyFacilityUtilizationPoint[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map((row): EnergyFacilityUtilizationPoint | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const d = r.asOfDate ?? r.AsOfDate;
      const u = r.utilizationPct ?? r.UtilizationPct;
      if (d == null) {
        return null;
      }
      const asOfDate = String(d).trim().slice(0, 10);
      if (u == null || u === '') {
        return { asOfDate, utilizationPct: null };
      }
      const utilizationPct = typeof u === 'number' ? u : Number(u);
      if (!Number.isFinite(utilizationPct)) {
        return { asOfDate, utilizationPct: null };
      }
      return { asOfDate, utilizationPct };
    })
    .filter((x): x is EnergyFacilityUtilizationPoint => x != null);
}

function normalizeWeightedRateTrendPoints(raw: unknown): ExposureWeightedRateTrendPoint[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map((row): ExposureWeightedRateTrendPoint | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const d = r.asOfDate ?? r.AsOfDate;
      const maxAdvanceCommitted = r.maxAdvanceRateCommittedPct ?? r.MaxAdvanceRateCommittedPct;
      const maxAdvanceFunded = r.maxAdvanceRateFundedPct ?? r.MaxAdvanceRateFundedPct;
      const marginCommitted = r.waUsedMarginCommittedPct ?? r.WaUsedMarginCommittedPct;
      const marginFunded = r.waUsedMarginFundedPct ?? r.WaUsedMarginFundedPct;
      const yieldRateFunded = r.waYieldRateFundedPct ?? r.WaYieldRateFundedPct;
      const oneMonthTermSofr = r.oneMonthTermSofrPct ?? r.OneMonthTermSofrPct;
      if (d == null) {
        return null;
      }
      const asOfDate = String(d).trim().slice(0, 10);
      return {
        asOfDate,
        maxAdvanceRateCommittedPct: numOrNull(maxAdvanceCommitted),
        maxAdvanceRateFundedPct: numOrNull(maxAdvanceFunded),
        waUsedMarginCommittedPct: numOrNull(marginCommitted),
        waUsedMarginFundedPct: numOrNull(marginFunded),
        waYieldRateFundedPct: numOrNull(yieldRateFunded),
        oneMonthTermSofrPct: numOrNull(oneMonthTermSofr),
      };
    })
    .filter((x): x is ExposureWeightedRateTrendPoint => x != null);
}

function normalizeQuarterlyFundedPoints(raw: unknown): EnergyQuarterlyFundedBalancePoint[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map((row): EnergyQuarterlyFundedBalancePoint | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const q = r.quarterLabel ?? r.QuarterLabel;
      const net = r.netUsedAmountUsd ?? r.NetUsedAmountUsd;
      const part = r.participationUsedAmountUsd ?? r.ParticipationUsedAmountUsd;
      if (q == null || net == null || part == null) {
        return null;
      }
      const quarterLabel = String(q).trim();
      const netN = typeof net === 'number' ? net : Number(net);
      const partN = typeof part === 'number' ? part : Number(part);
      if (!Number.isFinite(netN) || !Number.isFinite(partN)) {
        return null;
      }
      return { quarterLabel, netUsedAmountUsd: netN, participationUsedAmountUsd: partN };
    })
    .filter((x): x is EnergyQuarterlyFundedBalancePoint => x != null);
}

function normalizeEnergyQuarterlyFundedBridgeWalk(raw: unknown): EnergyQuarterlyFundedBalanceBridgeWalk | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const n = r.newOriginationsUsd ?? r.NewOriginationsUsd;
  const f = r.fundingUsd ?? r.FundingUsd;
  const p = r.paydownsUsd ?? r.PaydownsUsd;
  const priorLabel = r.priorQuarterLabel ?? r.PriorQuarterLabel;
  const currentLabel = r.currentQuarterLabel ?? r.CurrentQuarterLabel;
  if (n == null || f == null || p == null) {
    return null;
  }
  const nN = typeof n === 'number' ? n : Number(n);
  const fN = typeof f === 'number' ? f : Number(f);
  const pN = typeof p === 'number' ? p : Number(p);
  if (!Number.isFinite(nN) || !Number.isFinite(fN) || !Number.isFinite(pN)) {
    return null;
  }
  return {
    newOriginationsUsd: nN,
    fundingUsd: fN,
    paydownsUsd: pN,
    priorQuarterLabel: priorLabel == null ? '' : String(priorLabel).trim(),
    currentQuarterLabel: currentLabel == null ? '' : String(currentLabel).trim(),
  };
}

function normalizeEnergyQuarterlyFundedSeriesPayload(raw: unknown): EnergyQuarterlyFundedBalanceSeriesPayload {
  if (Array.isArray(raw)) {
    return { quarters: normalizeQuarterlyFundedPoints(raw), latestBridge: null };
  }
  if (!raw || typeof raw !== 'object' || raw instanceof Response) {
    return { quarters: [], latestBridge: null };
  }
  const o = raw as Record<string, unknown>;
  const quartersRaw = o.quarters ?? o.Quarters;
  const bridgeRaw = o.latestBridge ?? o.LatestBridge;
  return {
    quarters: normalizeQuarterlyFundedPoints(quartersRaw),
    latestBridge: normalizeEnergyQuarterlyFundedBridgeWalk(bridgeRaw),
  };
}

function mapExposureFacilityOptionsFromApiRows(listRaw: unknown[]): EnergyExposureFacilityOption[] {
  const deduped = dedupeExposureFacilityOptions(
    listRaw
      .map((row): EnergyExposureFacilityOption | null => {
        if (!row || typeof row !== 'object') {
          return null;
        }
        const r = row as Record<string, unknown>;
        const dealNameRaw = r.dealName ?? r.DealName;
        const displayRaw = r.displayLabel ?? r.DisplayLabel;
        if (dealNameRaw == null || displayRaw == null) {
          return null;
        }
        const dealName = String(dealNameRaw).trim();
        const displayLabel = String(displayRaw).trim();
        if (!dealName || !displayLabel) {
          return null;
        }
        const csIdRaw = r.csId ?? r.CsId ?? r.CS_ID ?? r.cs_id;
        const csId = csIdRaw == null ? null : String(csIdRaw).trim() || null;
        const terminatedRaw = r.isTerminated ?? r.IsTerminated;
        const isTerminated = terminatedRaw === true || terminatedRaw === 'true' || terminatedRaw === 1;
        return { dealName, csId, displayLabel, isTerminated };
      })
      .filter((x): x is EnergyExposureFacilityOption => x != null),
  );
  return sortExposureFacilityOptions(deduped);
}

function normalizeFacilityConcentrationTop10(raw: unknown): EnergyFacilityConcentrationTop10Response | null {
  if (!raw || typeof raw !== 'object' || raw instanceof Response) {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const asOf = o.asOfDate ?? o.AsOfDate;
  const total = o.portfolioTotalGrossUsedUsd ?? o.PortfolioTotalGrossUsedUsd;
  const top10Pct = o.top10CombinedSharePct ?? o.Top10CombinedSharePct;
  const facRaw = o.facilities ?? o.Facilities;
  if (asOf == null || total == null) {
    return null;
  }
  const facilities: EnergyFacilityConcentrationFacilityRow[] = Array.isArray(facRaw)
    ? facRaw
        .map((row): EnergyFacilityConcentrationFacilityRow | null => {
          if (!row || typeof row !== 'object') {
            return null;
          }
          const r = row as Record<string, unknown>;
          const name = r.borrowerName ?? r.BorrowerName;
          const gross = r.grossUsedAmountUsd ?? r.GrossUsedAmountUsd;
          const pct = r.pctOfPortfolioTotal ?? r.PctOfPortfolioTotal;
          if (name == null || gross == null || pct == null) {
            return null;
          }
          const grossN = typeof gross === 'number' ? gross : Number(gross);
          const pctN = typeof pct === 'number' ? pct : Number(pct);
          if (!Number.isFinite(grossN) || !Number.isFinite(pctN)) {
            return null;
          }
          return {
            borrowerName: String(name).trim(),
            grossUsedAmountUsd: grossN,
            pctOfPortfolioTotal: pctN,
          };
        })
        .filter((x): x is EnergyFacilityConcentrationFacilityRow => x != null)
    : [];
  const totalN = typeof total === 'number' ? total : Number(total);
  const top10N = top10Pct == null ? 0 : typeof top10Pct === 'number' ? top10Pct : Number(top10Pct);
  if (!Number.isFinite(totalN) || !Number.isFinite(top10N)) {
    return null;
  }
  return {
    asOfDate: String(asOf).trim().slice(0, 10),
    portfolioTotalGrossUsedUsd: totalN,
    top10CombinedSharePct: top10N,
    facilities,
  };
}

type RiskLimitStratificationDisplayMode = 'energy' | 'plain';

function normalizeRiskLimitStratificationRows(
  raw: unknown,
  displayMode: RiskLimitStratificationDisplayMode = 'energy',
): EnergyRiskLimitStratificationRow[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map((row): EnergyRiskLimitStratificationRow | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const limit = r.riskLimit ?? r.RiskLimit;
      const funded = r.funded ?? r.Funded;
      const commitment = r.commitment ?? r.Commitment;
      const qtd = r.qtdChange ?? r.QtdChange;
      const ytd = r.ytdChange ?? r.YtdChange;
      const part = r.participation ?? r.Participation;
      if (limit == null || funded == null || commitment == null || qtd == null || ytd == null || part == null) {
        return null;
      }
      const fundedN = typeof funded === 'number' ? funded : Number(funded);
      const commitmentN = typeof commitment === 'number' ? commitment : Number(commitment);
      const qtdN = typeof qtd === 'number' ? qtd : Number(qtd);
      const ytdN = typeof ytd === 'number' ? ytd : Number(ytd);
      const partN = typeof part === 'number' ? part : Number(part);
      if (
        !Number.isFinite(fundedN) ||
        !Number.isFinite(commitmentN) ||
        !Number.isFinite(qtdN) ||
        !Number.isFinite(ytdN) ||
        !Number.isFinite(partN)
      ) {
        return null;
      }
      const limitStr = String(limit).trim();
      const riskLimit = displayMode === 'plain' ? limitStr : displayEnergyRiskLimitBucket(limitStr);
      return {
        riskLimit,
        funded: fundedN,
        commitment: commitmentN,
        qtdChange: qtdN,
        ytdChange: ytdN,
        participation: partN,
      };
    })
    .filter((x): x is EnergyRiskLimitStratificationRow => x != null);
}

function normalizeFullPortfolioAssetClassSummaryRows(raw: unknown): FullPortfolioAssetClassSummaryRow[] {
  const list: unknown[] = Array.isArray(raw) ? raw : [];
  const displayAssetClass = (value: unknown): string => {
    const label = String(value ?? '').trim();
    if (label.toLowerCase() === 'cre') {
      return 'Non US CRE';
    }
    if (label.toLowerCase() === 'resi') {
      return 'Non US Resi';
    }
    return label;
  };
  const num = (value: unknown): number => {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  return list
    .map((row): FullPortfolioAssetClassSummaryRow | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const assetClass = r.assetClass ?? r.AssetClass;
      if (assetClass == null) {
        return null;
      }

      return {
        assetClass: displayAssetClass(assetClass),
        crmApprovedUsd: num(r.crmApprovedUsd ?? r.CrmApprovedUsd),
        committedAmountUsd: num(r.committedAmountUsd ?? r.CommittedAmountUsd),
        netExposureUsd: num(r.netExposureUsd ?? r.NetExposureUsd),
        usedAmountUsd: num(r.usedAmountUsd ?? r.UsedAmountUsd),
        waMaxAdvanceRateCommitPct: numOrNull(r.waMaxAdvanceRateCommitPct ?? r.WaMaxAdvanceRateCommitPct),
        waUsedMarginCommitPct: numOrNull(r.waUsedMarginCommitPct ?? r.WaUsedMarginCommitPct),
        waUsedMarginFundedPct: numOrNull(r.waUsedMarginFundedPct ?? r.WaUsedMarginFundedPct),
        waYearsRemainDp: numOrNull(r.waYearsRemainDp ?? r.WaYearsRemainDp),
      };
    })
    .filter((row): row is FullPortfolioAssetClassSummaryRow => row != null);
}

function normalizeConcentrationMetrics(raw: unknown, fallbackAsOf: string): EnergyConcentrationMetrics | null {
  if (!raw || typeof raw !== 'object' || raw instanceof Response) {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const asOf = o.asOfDate ?? o.AsOfDate ?? fallbackAsOf;
  const active = o.activeFacilities ?? o.ActiveFacilities;
  const largestUsd = o.largestSingleFacilityUsd ?? o.LargestSingleFacilityUsd;
  const largestShare = o.largestSingleFacilityShare ?? o.LargestSingleFacilityShare;
  const top5 = o.top5Share ?? o.Top5Share;
  const top10 = o.top10Share ?? o.Top10Share;
  if (active == null || largestUsd == null || largestShare == null || top5 == null || top10 == null) {
    return null;
  }
  const activeN = typeof active === 'number' ? active : Number(active);
  const largestUsdN = typeof largestUsd === 'number' ? largestUsd : Number(largestUsd);
  const largestShareN = typeof largestShare === 'number' ? largestShare : Number(largestShare);
  const top5N = typeof top5 === 'number' ? top5 : Number(top5);
  const top10N = typeof top10 === 'number' ? top10 : Number(top10);
  if (
    !Number.isFinite(activeN) ||
    !Number.isFinite(largestUsdN) ||
    !Number.isFinite(largestShareN) ||
    !Number.isFinite(top5N) ||
    !Number.isFinite(top10N)
  ) {
    return null;
  }
  return {
    asOfDate: String(asOf).trim().slice(0, 10),
    activeFacilities: Math.round(activeN),
    largestSingleFacilityUsd: largestUsdN,
    largestSingleFacilityShare: largestShareN,
    top5Share: top5N,
    top10Share: top10N,
  };
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === '') {
    return null;
  }
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeTopCounterpartiesRows(raw: unknown): EnergyTopCounterpartyRow[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map((row): EnergyTopCounterpartyRow | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const name = r.clientName ?? r.ClientName;
      const funded = r.funded ?? r.Funded;
      const fundedPct = r.fundedPct ?? r.FundedPct;
      const netExposure = r.netExposure ?? r.NetExposure;
      const netExposurePct = r.netExposurePct ?? r.NetExposurePct;
      const committed = r.committed ?? r.Committed;
      const committedPct = r.committedPct ?? r.CommittedPct;
      const participation = r.participation ?? r.Participation;
      const participationPct = r.participationPct ?? r.ParticipationPct;
      if (name == null || funded == null || netExposure == null || committed == null || participation == null) {
        return null;
      }
      const fundedN = typeof funded === 'number' ? funded : Number(funded);
      const netN = typeof netExposure === 'number' ? netExposure : Number(netExposure);
      const committedN = typeof committed === 'number' ? committed : Number(committed);
      const partN = typeof participation === 'number' ? participation : Number(participation);
      if (
        !Number.isFinite(fundedN) ||
        !Number.isFinite(netN) ||
        !Number.isFinite(committedN) ||
        !Number.isFinite(partN)
      ) {
        return null;
      }
      return {
        clientName: String(name).trim(),
        funded: fundedN,
        fundedPct: numOrNull(fundedPct),
        netExposure: netN,
        netExposurePct: numOrNull(netExposurePct),
        committed: committedN,
        committedPct: numOrNull(committedPct),
        participation: partN,
        participationPct: numOrNull(participationPct),
      };
    })
    .filter((x): x is EnergyTopCounterpartyRow => x != null);
}

type DealMoverRowDisplayMode = 'energy' | 'plain';

function mapQtdTopIncreaseRow(
  row: Record<string, unknown>,
  displayMode: DealMoverRowDisplayMode = 'energy',
): EnergyQtdTopIncreaseRow | null {
  const deal = row.dealName ?? row.DealName;
  const bucket = row.riskLimitBucket ?? row.RiskLimitBucket;
  const used = row.usedBalanceUsd ?? row.UsedBalanceUsd;
  const usedCh = row.usedChangeUsd ?? row.UsedChangeUsd ?? row.qtdFundingChangeUsd ?? row.QtdFundingChangeUsd;
  const committedCh = row.committedChangeUsd ?? row.CommittedChangeUsd;
  if (deal == null || bucket == null || used == null || usedCh == null || committedCh == null) {
    return null;
  }
  const usedN = typeof used === 'number' ? used : Number(used);
  const usedChN = typeof usedCh === 'number' ? usedCh : Number(usedCh);
  const committedChN = typeof committedCh === 'number' ? committedCh : Number(committedCh);
  if (!Number.isFinite(usedN) || !Number.isFinite(usedChN) || !Number.isFinite(committedChN)) {
    return null;
  }
  const bucketStr = String(bucket).trim();
  const riskLimitBucket = displayMode === 'plain' ? bucketStr : displayEnergyRiskLimitBucket(bucketStr);
  return {
    dealName: String(deal).trim(),
    riskLimitBucket,
    usedBalanceUsd: usedN,
    usedChangeUsd: usedChN,
    committedChangeUsd: committedChN,
  };
}

function normalizeEnergyDealMoversPayload(
  raw: unknown,
  displayMode: DealMoverRowDisplayMode = 'energy',
): EnergyQtdTopIncreasesPayload {
  const empty: EnergyQtdTopIncreasesPayload = { priorPeriodLabel: '', rows: [] };
  if (raw == null || typeof raw !== 'object' || raw instanceof Response) {
    return empty;
  }
  const root = raw as Record<string, unknown>;
  const body = root.data ?? root.Data ?? raw;
  if (Array.isArray(body)) {
    return {
      priorPeriodLabel: '',
      rows: body
        .map((row): EnergyQtdTopIncreaseRow | null =>
          row && typeof row === 'object' ? mapQtdTopIncreaseRow(row as Record<string, unknown>, displayMode) : null,
        )
        .filter((x): x is EnergyQtdTopIncreaseRow => x != null),
    };
  }
  if (body && typeof body === 'object') {
    const o = body as Record<string, unknown>;
    const labelRaw = o.priorPeriodLabel ?? o.PriorPeriodLabel;
    const priorPeriodLabel = typeof labelRaw === 'string' ? labelRaw.trim() : '';
    const rowsRaw = o.rows ?? o.Rows;
    if (Array.isArray(rowsRaw)) {
      return {
        priorPeriodLabel,
        rows: rowsRaw
          .map((row): EnergyQtdTopIncreaseRow | null =>
            row && typeof row === 'object' ? mapQtdTopIncreaseRow(row as Record<string, unknown>, displayMode) : null,
          )
          .filter((x): x is EnergyQtdTopIncreaseRow => x != null),
      };
    }
  }
  return empty;
}

function normalizeLargeMovementRows(raw: unknown): EnergyLargeMovementRow[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map((row): EnergyLargeMovementRow | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const asOf = r.asOfDate ?? r.AsOfDate;
      const deal = r.dealName ?? r.DealName;
      const amt = r.usedDeltaUsd ?? r.UsedDeltaUsd ?? r.amount ?? r.Amount;
      const committed = r.committedDeltaUsd ?? r.CommittedDeltaUsd ?? r.committedDelta ?? r.CommittedDelta;
      const csId = r.csId ?? r.CsId ?? r.CS_ID ?? r.cs_id;
      const userTag = r.userTag ?? r.UserTag ?? r.userCategory ?? r.UserCategory ?? r.USER_TAG ?? r.USER_CATEGORY;
      if (asOf == null || deal == null || amt == null || committed == null) {
        return null;
      }
      const amtN = typeof amt === 'number' ? amt : Number(amt);
      const committedN = typeof committed === 'number' ? committed : Number(committed);
      if (!Number.isFinite(amtN) || !Number.isFinite(committedN)) {
        return null;
      }
      return {
        asOfDate: String(asOf).trim().slice(0, 10),
        deal: String(deal).trim(),
        amount: amtN,
        committedDelta: committedN,
        ...(csId != null && String(csId).trim() !== ''
          ? { csId: typeof csId === 'number' ? csId : String(csId).trim() }
          : {}),
        ...(userTag != null && String(userTag).trim() !== '' ? { userTag: String(userTag).trim() } : {}),
      };
    })
    .filter((x): x is EnergyLargeMovementRow => x != null);
}

function normalizeEnergyInternalRatingMigrationCells(raw: unknown): EnergyInternalRatingMigrationCell[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map((row): EnergyInternalRatingMigrationCell | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const prior = r.priorRating ?? r.PriorRating;
      const current = r.currentRating ?? r.CurrentRating;
      const cnt = r.facilityCount ?? r.FacilityCount;
      if (prior == null || current == null || cnt == null) {
        return null;
      }
      const cntN = typeof cnt === 'number' ? cnt : Number(cnt);
      if (!Number.isFinite(cntN)) {
        return null;
      }
      return {
        priorRating: String(prior).trim(),
        currentRating: String(current).trim(),
        facilityCount: cntN,
      };
    })
    .filter((x): x is EnergyInternalRatingMigrationCell => x != null);
}

function normalizeEnergyInternalRatingChangeRows(raw: unknown): EnergyInternalRatingChangeRow[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map((row): EnergyInternalRatingChangeRow | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const borrower = r.borrowerName ?? r.BorrowerName ?? r.dealName ?? r.DealName;
      const used = r.usedUsd ?? r.UsedUsd;
      const prior = r.priorRating ?? r.PriorRating;
      const current = r.currentRating ?? r.CurrentRating;
      if (borrower == null || used == null || current == null) {
        return null;
      }
      const usedN = typeof used === 'number' ? used : Number(used);
      if (!Number.isFinite(usedN)) {
        return null;
      }
      const priorS = String(prior ?? '').trim();
      const currentS = String(current).trim();
      const meta = internalRatingDirectionMeta(priorS, currentS);
      return {
        borrowerName: String(borrower).trim(),
        usedUsd: usedN,
        prior: priorS,
        current: currentS,
        direction: meta.label,
        directionTone: meta.tone,
      };
    })
    .filter((x): x is EnergyInternalRatingChangeRow => x != null);
}

function normalizeRiskLimitConcentrationRows(
  raw: unknown,
  displayMode: RiskLimitConcentrationDisplayMode = 'energy',
): EnergyRiskLimitConcentrationRow[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  const parsed = list
    .map((row): EnergyRiskLimitConcentrationRow | null => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const r = row as Record<string, unknown>;
      const asOf = r.asOfDate ?? r.AsOfDate;
      const quarter = r.quarter ?? r.Quarter;
      const bucket = r.riskLimitBucket ?? r.RiskLimitBucket;
      const funded = r.fundedUsd ?? r.FundedUsd;
      if (asOf == null || quarter == null || bucket == null || funded == null) {
        return null;
      }
      const fundedN = typeof funded === 'number' ? funded : Number(funded);
      if (!Number.isFinite(fundedN)) {
        return null;
      }
      return {
        asOfDate: String(asOf).trim().slice(0, 10),
        quarter: String(quarter).trim(),
        riskLimitBucket: String(bucket).trim(),
        fundedUsd: fundedN,
      };
    })
    .filter((x): x is EnergyRiskLimitConcentrationRow => x != null);
  return mergeEnergyRiskLimitConcentrationRows(parsed, displayMode);
}

/** Same JSON shape as Energy exposure KPIs (CLS facility terms aggregates). */
export type TransportationExposureKpis = EnergyExposureKpis;

/** Same point shape as Energy exposure daily balance chart; scoped to Transportation portfolio on the API. */
export type TransportationDailyPortfolioBalancePoint = EnergyDailyPortfolioBalancePoint;

/** Same point shape as Energy exposure quarterly chart; scoped to Transportation portfolio on the API. */
export type TransportationQuarterlyFundedBalancePoint = EnergyQuarterlyFundedBalancePoint;

/** Same payload shape as Energy quarterly series (quarters + optional gross-funded bridge walk). */
export type TransportationQuarterlyFundedBalanceSeriesPayload = EnergyQuarterlyFundedBalanceSeriesPayload;

/** Same JSON shape as Energy facility concentration; API scopes to Transportation portfolio. */
export type TransportationFacilityConcentrationTop10Response = EnergyFacilityConcentrationTop10Response;

export type ExposureCurrentPositionRow = {
  dealName: string;
  csId: string | null;
  assetClass: string | null;
  riskLimit: string | null;
  riskBook: string | null;
  counterparty: string | null;
  clientName: string | null;
  commitmentUsd: number;
  usedAmountUsd: number;
  participationUsd: number;
  utilizationPct: number | null;
  marginPlusCasRate: number | null;
  unusedRate: number | null;
  internalRatingPrior: string | null;
  internalRatingCurrent: string | null;
  qtdFundingUsd: number;
  ttmFundingUsd: number;
  ytdFundingUsd: number;
  qtdCommittedChangeUsd: number;
  ytdCommittedChangeUsd: number;
};

function mapExposureCurrentPositionRow(raw: Record<string, unknown>): ExposureCurrentPositionRow | null {
  const dealName = raw.dealName ?? raw.DealName;
  if (dealName == null || String(dealName).trim() === '') {
    return null;
  }

  const num = (key: string, alt: string) => {
    const v = raw[key] ?? raw[alt];
    if (v == null || v === '') {
      return 0;
    }
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const optNum = (key: string, alt: string): number | null => {
    const v = raw[key] ?? raw[alt];
    if (v == null || v === '') {
      return null;
    }
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const optStr = (key: string, alt: string): string | null => {
    const v = raw[key] ?? raw[alt];
    if (v == null) {
      return null;
    }
    const s = String(v).trim();
    return s === '' ? null : s;
  };

  return {
    dealName: String(dealName).trim(),
    csId: optStr('csId', 'CsId') ?? optStr('CS_ID', 'cs_id'),
    assetClass: optStr('assetClass', 'AssetClass'),
    riskLimit: optStr('riskLimit', 'RiskLimit'),
    riskBook: optStr('riskBook', 'RiskBook'),
    counterparty: optStr('counterparty', 'Counterparty'),
    clientName: optStr('clientName', 'ClientName'),
    commitmentUsd: num('commitmentUsd', 'CommitmentUsd'),
    usedAmountUsd: num('usedAmountUsd', 'UsedAmountUsd'),
    participationUsd: num('participationUsd', 'ParticipationUsd'),
    utilizationPct: optNum('utilizationPct', 'UtilizationPct'),
    marginPlusCasRate: optNum('marginPlusCasRate', 'MarginPlusCasRate'),
    unusedRate: optNum('unusedRate', 'UnusedRate'),
    internalRatingPrior: optStr('internalRatingPrior', 'InternalRatingPrior'),
    internalRatingCurrent: optStr('internalRatingCurrent', 'InternalRatingCurrent'),
    qtdFundingUsd: num('qtdFundingUsd', 'QtdFundingUsd'),
    ttmFundingUsd: num('ttmFundingUsd', 'TtmFundingUsd'),
    ytdFundingUsd: num('ytdFundingUsd', 'YtdFundingUsd'),
    qtdCommittedChangeUsd: num('qtdCommittedChangeUsd', 'QtdCommittedChangeUsd'),
    ytdCommittedChangeUsd: num('ytdCommittedChangeUsd', 'YtdCommittedChangeUsd'),
  };
}

function normalizeExposureCurrentPositionsPayload(raw: unknown): ExposureCurrentPositionRow[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? (() => {
          const o = raw as Record<string, unknown>;
          const inner = o.rows ?? o.Rows ?? o.data ?? o.Data;
          return Array.isArray(inner) ? inner : [];
        })()
      : [];
  return list
    .map(row => (row && typeof row === 'object' ? mapExposureCurrentPositionRow(row as Record<string, unknown>) : null))
    .filter((x): x is ExposureCurrentPositionRow => x != null);
}

export type EnergySolarExposureChangeSummaryRow = {
  csId: string;
  dealName: string;
  crmApprovedT0Usd: number;
  crmApprovedT1Usd: number;
  committedT0Usd: number;
  committedT1Usd: number;
  commChangeUsd: number;
  fundedT0Usd: number;
  fundedT1Usd: number;
  fundedChangeUsd: number;
};

export type EnergySolarExposureChangesSummaryPayload = {
  t0Date: string;
  t1Date: string | null;
  rows: EnergySolarExposureChangeSummaryRow[];
};

export type EnergySolarFundingVehicleChangeRow = {
  fundingVehicle: string;
  committedT0Usd: number;
  committedT1Usd: number;
  commChangeUsd: number;
  fundedT0Usd: number;
  fundedT1Usd: number;
  fundedChangeUsd: number;
};

export type EnergySolarExposureChangesByFundingVehiclePayload = {
  t0Date: string;
  t1Date: string | null;
  rows: EnergySolarFundingVehicleChangeRow[];
};

function readSolarExposurePayloadDate(body: Record<string, unknown>, camel: string, pascal: string): string | null {
  const v = body[camel] ?? body[pascal];
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function readSolarExposureUsdField(raw: Record<string, unknown>, camel: string, pascal: string): number {
  const v = raw[camel] ?? raw[pascal];
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function mapSolarExposureChangeSummaryRow(raw: Record<string, unknown>): EnergySolarExposureChangeSummaryRow | null {
  const csId = String(raw.csId ?? raw.CsId ?? '').trim();
  const dealName = String(raw.dealName ?? raw.DealName ?? '').trim();
  if (!csId && !dealName) return null;
  return {
    csId,
    dealName,
    crmApprovedT0Usd: readSolarExposureUsdField(raw, 'crmApprovedT0Usd', 'CrmApprovedT0Usd'),
    crmApprovedT1Usd: readSolarExposureUsdField(raw, 'crmApprovedT1Usd', 'CrmApprovedT1Usd'),
    committedT0Usd: readSolarExposureUsdField(raw, 'committedT0Usd', 'CommittedT0Usd'),
    committedT1Usd: readSolarExposureUsdField(raw, 'committedT1Usd', 'CommittedT1Usd'),
    commChangeUsd: readSolarExposureUsdField(raw, 'commChangeUsd', 'CommChangeUsd'),
    fundedT0Usd: readSolarExposureUsdField(raw, 'fundedT0Usd', 'FundedT0Usd'),
    fundedT1Usd: readSolarExposureUsdField(raw, 'fundedT1Usd', 'FundedT1Usd'),
    fundedChangeUsd: readSolarExposureUsdField(raw, 'fundedChangeUsd', 'FundedChangeUsd'),
  };
}

function normalizeSolarExposureChangesSummaryPayload(raw: unknown): EnergySolarExposureChangesSummaryPayload {
  if (!raw || typeof raw !== 'object' || raw instanceof Response) {
    return { t0Date: '', t1Date: null, rows: [] };
  }
  const body = raw as Record<string, unknown>;
  const listRaw = body.rows ?? body.Rows;
  const rows = Array.isArray(listRaw)
    ? listRaw
        .map(row =>
          row && typeof row === 'object' ? mapSolarExposureChangeSummaryRow(row as Record<string, unknown>) : null,
        )
        .filter((x): x is EnergySolarExposureChangeSummaryRow => x != null)
    : [];
  return {
    t0Date: readSolarExposurePayloadDate(body, 't0Date', 'T0Date') ?? '',
    t1Date: readSolarExposurePayloadDate(body, 't1Date', 'T1Date'),
    rows,
  };
}

function mapSolarFundingVehicleChangeRow(raw: Record<string, unknown>): EnergySolarFundingVehicleChangeRow | null {
  const fundingVehicle = String(raw.fundingVehicle ?? raw.FundingVehicle ?? '').trim();
  if (!fundingVehicle) return null;
  return {
    fundingVehicle,
    committedT0Usd: readSolarExposureUsdField(raw, 'committedT0Usd', 'CommittedT0Usd'),
    committedT1Usd: readSolarExposureUsdField(raw, 'committedT1Usd', 'CommittedT1Usd'),
    commChangeUsd: readSolarExposureUsdField(raw, 'commChangeUsd', 'CommChangeUsd'),
    fundedT0Usd: readSolarExposureUsdField(raw, 'fundedT0Usd', 'FundedT0Usd'),
    fundedT1Usd: readSolarExposureUsdField(raw, 'fundedT1Usd', 'FundedT1Usd'),
    fundedChangeUsd: readSolarExposureUsdField(raw, 'fundedChangeUsd', 'FundedChangeUsd'),
  };
}

function normalizeSolarExposureChangesByFundingVehiclePayload(
  raw: unknown,
): EnergySolarExposureChangesByFundingVehiclePayload {
  if (!raw || typeof raw !== 'object' || raw instanceof Response) {
    return { t0Date: '', t1Date: null, rows: [] };
  }
  const body = raw as Record<string, unknown>;
  const listRaw = body.rows ?? body.Rows;
  const rows = Array.isArray(listRaw)
    ? listRaw
        .map(row =>
          row && typeof row === 'object' ? mapSolarFundingVehicleChangeRow(row as Record<string, unknown>) : null,
        )
        .filter((x): x is EnergySolarFundingVehicleChangeRow => x != null)
    : [];
  return {
    t0Date: readSolarExposurePayloadDate(body, 't0Date', 'T0Date') ?? '',
    t1Date: readSolarExposurePayloadDate(body, 't1Date', 'T1Date'),
    rows,
  };
}

export type AutoExposureKpis = EnergyExposureKpis;
export type AutoDailyPortfolioBalancePoint = EnergyDailyPortfolioBalancePoint;
export type AutoQuarterlyFundedBalanceSeriesPayload = EnergyQuarterlyFundedBalanceSeriesPayload;
export type AutoFacilityConcentrationTop10Response = EnergyFacilityConcentrationTop10Response;

export type ApacExposureKpis = EnergyExposureKpis;
export type ApacDailyPortfolioBalancePoint = EnergyDailyPortfolioBalancePoint;
export type ApacQuarterlyFundedBalanceSeriesPayload = EnergyQuarterlyFundedBalanceSeriesPayload;
export type ApacFacilityConcentrationTop10Response = EnergyFacilityConcentrationTop10Response;

export type StratFinDeskExposureKpis = EnergyExposureKpis;
export type StratFinDeskDailyPortfolioBalancePoint = EnergyDailyPortfolioBalancePoint;
export type StratFinDeskQuarterlyFundedBalanceSeriesPayload = EnergyQuarterlyFundedBalanceSeriesPayload;
export type StratFinDeskFacilityConcentrationTop10Response = EnergyFacilityConcentrationTop10Response;

export type EmeaExposureKpis = EnergyExposureKpis;
export type EmeaDailyPortfolioBalancePoint = EnergyDailyPortfolioBalancePoint;
export type EmeaQuarterlyFundedBalanceSeriesPayload = EnergyQuarterlyFundedBalanceSeriesPayload;
export type EmeaFacilityConcentrationTop10Response = EnergyFacilityConcentrationTop10Response;

export type MarketplaceExposureKpis = EnergyExposureKpis;
export type FundFinanceExposureKpis = EnergyExposureKpis;
export type MarketplaceDailyPortfolioBalancePoint = EnergyDailyPortfolioBalancePoint;
export type FundFinanceDailyPortfolioBalancePoint = EnergyDailyPortfolioBalancePoint;
export type MarketplaceQuarterlyFundedBalanceSeriesPayload = EnergyQuarterlyFundedBalanceSeriesPayload;
export type FundFinanceQuarterlyFundedBalanceSeriesPayload = EnergyQuarterlyFundedBalanceSeriesPayload;
export type MarketplaceFacilityConcentrationTop10Response = EnergyFacilityConcentrationTop10Response;
export type FundFinanceFacilityConcentrationTop10Response = EnergyFacilityConcentrationTop10Response;

export class ExposureService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  fetchExposureWeightedRateTrendSeries = async (path: string, dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({ path: path, params } as never);
    return normalizeWeightedRateTrendPoints(raw);
  };

  fetchEnergyExposureDailyPortfolioBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: ENERGY_DAILY_PORTFOLIO_BALANCE_PATH,
      params,
    } as never);
    return normalizeDailyPortfolioPoints(raw);
  };

  fetchEnergyFacilityUtilizationSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({ path: ENERGY_FACILITY_UTILIZATION_PATH, params } as never);
    return normalizeFacilityUtilizationPoints(raw);
  };

  fetchEnergyWeightedRateTrendSeries = async (dealName?: string, asOfDate?: string) => {
    return this.fetchExposureWeightedRateTrendSeries(ENERGY_WEIGHTED_RATE_TREND_PATH, dealName, asOfDate);
  };

  fetchTransportationFacilityUtilizationSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_FACILITY_UTILIZATION_PATH,
      params,
    } as never);
    return normalizeFacilityUtilizationPoints(raw);
  };

  fetchTransportationWeightedRateTrendSeries = async (dealName?: string, asOfDate?: string) => {
    return this.fetchExposureWeightedRateTrendSeries(TRANSPORTATION_WEIGHTED_RATE_TREND_PATH, dealName, asOfDate);
  };

  fetchEnergyExposureQuarterlyFundedBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: ENERGY_QUARTERLY_FUNDED_BALANCE_PATH,
      params,
    } as never);
    return normalizeEnergyQuarterlyFundedSeriesPayload(raw);
  };

  fetchEnergyExposureFacilityNames = async () => {
    const raw = await this.get<unknown>({ path: ENERGY_FACILITIES_PATH } as never);
    if (!raw || typeof raw !== 'object' || raw instanceof Response) {
      return [];
    }
    const body = raw as Record<string, unknown>;
    const listRaw = body.facilities ?? body.Facilities;
    if (!Array.isArray(listRaw)) {
      return [];
    }
    return mapExposureFacilityOptionsFromApiRows(listRaw);
  };

  fetchTransportationExposureFacilityNames = async () => {
    const raw = await this.get<unknown>({ path: TRANSPORTATION_FACILITIES_PATH } as never);
    if (!raw || typeof raw !== 'object' || raw instanceof Response) {
      return [];
    }
    const body = raw as Record<string, unknown>;
    const listRaw = body.facilities ?? body.Facilities;
    if (!Array.isArray(listRaw)) {
      return [];
    }
    return mapExposureFacilityOptionsFromApiRows(listRaw);
  };

  fetchEnergyConcentrationMetrics = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_CONCENTRATION_METRICS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeConcentrationMetrics(raw, asOfDate);
  };

  fetchTransportationConcentrationMetrics = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_CONCENTRATION_METRICS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeConcentrationMetrics(raw, asOfDate);
  };

  fetchEnergyTopCounterparties = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_TOP_COUNTERPARTIES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeTopCounterpartiesRows(raw);
  };

  fetchTransportationTopCounterparties = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_TOP_COUNTERPARTIES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeTopCounterpartiesRows(raw);
  };

  fetchEnergyQtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_QTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'energy');
  };

  fetchEnergyQtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_QTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'energy');
  };

  fetchEnergyYtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_YTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'energy');
  };

  fetchEnergyYtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_YTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'energy');
  };

  fetchTransportationQtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_QTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchTransportationQtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_QTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchTransportationYtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_YTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchTransportationYtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_YTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchEnergyLargeFundingsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_LARGE_FUNDINGS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchEnergyLargePaydownsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_LARGE_PAYDOWNS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchTransportationLargeFundingsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_LARGE_FUNDINGS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchTransportationLargePaydownsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_LARGE_PAYDOWNS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchEnergyInternalRatingMigration = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_INTERNAL_RATING_MIGRATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingMigrationCells(raw);
  };

  fetchEnergyInternalRatingChanges = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_INTERNAL_RATING_CHANGES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingChangeRows(raw);
  };

  fetchTransportationInternalRatingMigration = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_INTERNAL_RATING_MIGRATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingMigrationCells(raw);
  };

  fetchTransportationInternalRatingChanges = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_INTERNAL_RATING_CHANGES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingChangeRows(raw);
  };

  fetchEnergyRiskLimitConcentrationSeries = async (asOfDate?: string) => {
    const params = asOfDate ? { asOfDate } : undefined;
    const raw = await this.get<unknown>({
      path: ENERGY_RISK_LIMIT_CONCENTRATION_PATH,
      params,
    } as never);
    return normalizeRiskLimitConcentrationRows(raw, 'energy');
  };

  fetchTransportationRiskLimitConcentrationSeries = async (asOfDate?: string) => {
    const params = asOfDate ? { asOfDate } : undefined;
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_RISK_LIMIT_CONCENTRATION_PATH,
      params,
    } as never);
    return normalizeRiskLimitConcentrationRows(raw, 'plain');
  };

  fetchEnergyRiskLimitStratification = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_RISK_LIMIT_STRATIFICATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeRiskLimitStratificationRows(raw, 'energy');
  };

  fetchTransportationRiskLimitStratification = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_RISK_LIMIT_STRATIFICATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeRiskLimitStratificationRows(raw, 'plain');
  };

  fetchEnergyFacilityConcentrationTop10 = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_FACILITY_CONCENTRATION_TOP10_PATH,
      params: { asOfDate },
    } as never);
    const parsed = normalizeFacilityConcentrationTop10(raw);
    if (!parsed) {
      return { asOfDate, portfolioTotalGrossUsedUsd: 0, top10CombinedSharePct: 0, facilities: [] };
    }
    return parsed;
  };

  fetchEnergyExposureAsOfDates = async () => {
    const raw = await this.get<unknown>({ path: ENERGY_AS_OF_DATES_PATH } as never);
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return stringArrayFromBody(raw as Record<string, unknown>, 'asOfDates', 'AsOfDates');
    }
    return [];
  };

  fetchEnergyExposureKpis = async (asOfDate: string) => {
    return await this.get<EnergyExposureKpis>({
      path: ENERGY_KPIS_PATH,
      params: { asOfDate },
    } as never);
  };

  fetchTransportationExposureAsOfDates = async () => {
    const raw = await this.get<unknown>({ path: TRANSPORTATION_AS_OF_DATES_PATH } as never);
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return stringArrayFromBody(raw as Record<string, unknown>, 'asOfDates', 'AsOfDates');
    }
    return [];
  };

  fetchTransportationExposureKpis = async (asOfDate: string) => {
    return await this.get<TransportationExposureKpis>({
      path: TRANSPORTATION_KPIS_PATH,
      params: { asOfDate },
    } as never);
  };

  fetchTransportationExposureDailyPortfolioBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_DAILY_PORTFOLIO_BALANCE_PATH,
      params,
    } as never);
    return normalizeDailyPortfolioPoints(raw);
  };

  fetchTransportationExposureQuarterlyFundedBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_QUARTERLY_FUNDED_BALANCE_PATH,
      params,
    } as never);
    return normalizeEnergyQuarterlyFundedSeriesPayload(raw);
  };

  fetchEnergyExposureCurrentPositions = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_CURRENT_POSITIONS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeExposureCurrentPositionsPayload(raw);
  };

  fetchEnergySolarExposureChangesSummary = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_SOLAR_EXPOSURE_CHANGES_SUMMARY_PATH,
      params: { asOfDate },
    } as never);
    return normalizeSolarExposureChangesSummaryPayload(raw);
  };

  fetchEnergySolarExposureChangesByFundingVehicle = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: ENERGY_SOLAR_EXPOSURE_CHANGES_BY_FUNDING_VEHICLE_PATH,
      params: { asOfDate },
    } as never);
    return normalizeSolarExposureChangesByFundingVehiclePayload(raw);
  };

  fetchTransportationExposureCurrentPositions = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_CURRENT_POSITIONS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeExposureCurrentPositionsPayload(raw);
  };

  fetchTransportationFacilityConcentrationTop10 = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: TRANSPORTATION_FACILITY_CONCENTRATION_TOP10_PATH,
      params: { asOfDate },
    } as never);
    const parsed = normalizeFacilityConcentrationTop10(raw);
    if (!parsed) {
      return { asOfDate, portfolioTotalGrossUsedUsd: 0, top10CombinedSharePct: 0, facilities: [] };
    }
    return parsed;
  };

  fetchAutoExposureAsOfDates = async () => {
    const raw = await this.get<unknown>({ path: AUTO_AS_OF_DATES_PATH } as never);
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return stringArrayFromBody(raw as Record<string, unknown>, 'asOfDates', 'AsOfDates');
    }
    return [];
  };

  fetchAutoExposureKpis = async (asOfDate: string) => {
    return await this.get<AutoExposureKpis>({ path: AUTO_KPIS_PATH, params: { asOfDate } } as never);
  };

  fetchAutoExposureDailyPortfolioBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({ path: AUTO_DAILY_PORTFOLIO_BALANCE_PATH, params } as never);
    return normalizeDailyPortfolioPoints(raw);
  };

  fetchAutoExposureQuarterlyFundedBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: AUTO_QUARTERLY_FUNDED_BALANCE_PATH,
      params,
    } as never);
    return normalizeEnergyQuarterlyFundedSeriesPayload(raw);
  };

  fetchAutoExposureFacilityNames = async () => {
    const raw = await this.get<unknown>({ path: AUTO_FACILITIES_PATH } as never);
    if (!raw || typeof raw !== 'object' || raw instanceof Response) {
      return [];
    }
    const body = raw as Record<string, unknown>;
    const listRaw = body.facilities ?? body.Facilities;
    if (!Array.isArray(listRaw)) {
      return [];
    }
    return mapExposureFacilityOptionsFromApiRows(listRaw);
  };

  fetchAutoFacilityUtilizationSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({ path: AUTO_FACILITY_UTILIZATION_PATH, params } as never);
    return normalizeFacilityUtilizationPoints(raw);
  };

  fetchAutoWeightedRateTrendSeries = async (dealName?: string, asOfDate?: string) => {
    return this.fetchExposureWeightedRateTrendSeries(AUTO_WEIGHTED_RATE_TREND_PATH, dealName, asOfDate);
  };

  fetchAutoFacilityConcentrationTop10 = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_FACILITY_CONCENTRATION_TOP10_PATH,
      params: { asOfDate },
    } as never);
    const parsed = normalizeFacilityConcentrationTop10(raw);
    if (!parsed) {
      return { asOfDate, portfolioTotalGrossUsedUsd: 0, top10CombinedSharePct: 0, facilities: [] };
    }
    return parsed;
  };

  fetchAutoRiskLimitStratification = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_RISK_LIMIT_STRATIFICATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeRiskLimitStratificationRows(raw, 'plain');
  };

  fetchAutoConcentrationMetrics = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_CONCENTRATION_METRICS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeConcentrationMetrics(raw, asOfDate);
  };

  fetchAutoTopCounterparties = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_TOP_COUNTERPARTIES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeTopCounterpartiesRows(raw);
  };

  fetchAutoRiskLimitConcentrationSeries = async (asOfDate?: string) => {
    const params = asOfDate ? { asOfDate } : undefined;
    const raw = await this.get<unknown>({
      path: AUTO_RISK_LIMIT_CONCENTRATION_PATH,
      params,
    } as never);
    return normalizeRiskLimitConcentrationRows(raw, 'plain');
  };

  fetchAutoQtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_QTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchAutoQtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_QTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchAutoYtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_YTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchAutoYtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_YTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchAutoLargeFundingsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_LARGE_FUNDINGS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchAutoLargePaydownsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_LARGE_PAYDOWNS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchAutoInternalRatingMigration = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_INTERNAL_RATING_MIGRATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingMigrationCells(raw);
  };

  fetchAutoInternalRatingChanges = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_INTERNAL_RATING_CHANGES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingChangeRows(raw);
  };

  fetchAutoExposureCurrentPositions = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: AUTO_CURRENT_POSITIONS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeExposureCurrentPositionsPayload(raw);
  };

  fetchApacExposureAsOfDates = async () => {
    const raw = await this.get<unknown>({ path: APAC_AS_OF_DATES_PATH } as never);
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return stringArrayFromBody(raw as Record<string, unknown>, 'asOfDates', 'AsOfDates');
    }
    return [];
  };

  fetchApacExposureKpis = async (asOfDate: string) => {
    return await this.get<ApacExposureKpis>({ path: APAC_KPIS_PATH, params: { asOfDate } } as never);
  };

  fetchApacExposureDailyPortfolioBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({ path: APAC_DAILY_PORTFOLIO_BALANCE_PATH, params } as never);
    return normalizeDailyPortfolioPoints(raw);
  };

  fetchApacExposureQuarterlyFundedBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: APAC_QUARTERLY_FUNDED_BALANCE_PATH,
      params,
    } as never);
    return normalizeEnergyQuarterlyFundedSeriesPayload(raw);
  };

  fetchApacExposureFacilityNames = async () => {
    const raw = await this.get<unknown>({ path: APAC_FACILITIES_PATH } as never);
    if (!raw || typeof raw !== 'object' || raw instanceof Response) {
      return [];
    }
    const body = raw as Record<string, unknown>;
    const listRaw = body.facilities ?? body.Facilities;
    if (!Array.isArray(listRaw)) {
      return [];
    }
    return mapExposureFacilityOptionsFromApiRows(listRaw);
  };

  fetchApacFacilityUtilizationSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({ path: APAC_FACILITY_UTILIZATION_PATH, params } as never);
    return normalizeFacilityUtilizationPoints(raw);
  };

  fetchApacWeightedRateTrendSeries = async (dealName?: string, asOfDate?: string) => {
    return this.fetchExposureWeightedRateTrendSeries(APAC_WEIGHTED_RATE_TREND_PATH, dealName, asOfDate);
  };

  fetchApacFacilityConcentrationTop10 = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_FACILITY_CONCENTRATION_TOP10_PATH,
      params: { asOfDate },
    } as never);
    const parsed = normalizeFacilityConcentrationTop10(raw);
    if (!parsed) {
      return { asOfDate, portfolioTotalGrossUsedUsd: 0, top10CombinedSharePct: 0, facilities: [] };
    }
    return parsed;
  };

  fetchApacRiskLimitStratification = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_RISK_LIMIT_STRATIFICATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeRiskLimitStratificationRows(raw, 'plain');
  };

  fetchApacConcentrationMetrics = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_CONCENTRATION_METRICS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeConcentrationMetrics(raw, asOfDate);
  };

  fetchApacTopCounterparties = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_TOP_COUNTERPARTIES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeTopCounterpartiesRows(raw);
  };

  fetchApacRiskLimitConcentrationSeries = async (asOfDate?: string) => {
    const params = asOfDate ? { asOfDate } : undefined;
    const raw = await this.get<unknown>({
      path: APAC_RISK_LIMIT_CONCENTRATION_PATH,
      params,
    } as never);
    return normalizeRiskLimitConcentrationRows(raw, 'plain');
  };

  fetchApacQtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_QTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchApacQtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_QTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchApacYtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_YTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchApacYtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_YTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchApacLargeFundingsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_LARGE_FUNDINGS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchApacLargePaydownsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_LARGE_PAYDOWNS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchApacInternalRatingMigration = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_INTERNAL_RATING_MIGRATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingMigrationCells(raw);
  };

  fetchApacInternalRatingChanges = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_INTERNAL_RATING_CHANGES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingChangeRows(raw);
  };

  fetchApacExposureCurrentPositions = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: APAC_CURRENT_POSITIONS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeExposureCurrentPositionsPayload(raw);
  };

  fetchStratFinDeskExposureAsOfDates = async (segmentSlug: StratFinDeskExposureSegmentSlug) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'as-of-dates'),
    } as never);
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return stringArrayFromBody(raw as Record<string, unknown>, 'asOfDates', 'AsOfDates');
    }
    return [];
  };

  fetchStratFinDeskExposureKpis = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    return await this.get<StratFinDeskExposureKpis>({
      path: stratFinDeskExposurePath(segmentSlug, 'kpis'),
      params: { asOfDate },
    } as never);
  };

  fetchFullPortfolioAssetClassSummary = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath('full-portfolio', 'asset-class-summary'),
      params: { asOfDate },
    } as never);
    return normalizeFullPortfolioAssetClassSummaryRows(raw);
  };

  fetchStratFinDeskExposureDailyPortfolioBalanceSeries = async (
    segmentSlug: StratFinDeskExposureSegmentSlug,
    dealName?: string,
    asOfDate?: string,
  ) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'daily-portfolio-balance-series'),
      params,
    } as never);
    return normalizeDailyPortfolioPoints(raw);
  };

  fetchStratFinDeskExposureQuarterlyFundedBalanceSeries = async (
    segmentSlug: StratFinDeskExposureSegmentSlug,
    dealName?: string,
    asOfDate?: string,
  ) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'quarterly-funded-balance-series'),
      params,
    } as never);
    return normalizeEnergyQuarterlyFundedSeriesPayload(raw);
  };

  fetchStratFinDeskExposureFacilityNames = async (segmentSlug: StratFinDeskExposureSegmentSlug) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'facilities'),
    } as never);
    if (!raw || typeof raw !== 'object' || raw instanceof Response) {
      return [];
    }
    const body = raw as Record<string, unknown>;
    const listRaw = body.facilities ?? body.Facilities;
    if (!Array.isArray(listRaw)) {
      return [];
    }
    return mapExposureFacilityOptionsFromApiRows(listRaw);
  };

  fetchStratFinDeskFacilityUtilizationSeries = async (
    segmentSlug: StratFinDeskExposureSegmentSlug,
    dealName?: string,
    asOfDate?: string,
  ) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'facility-utilization-series'),
      params,
    } as never);
    return normalizeFacilityUtilizationPoints(raw);
  };

  fetchStratFinDeskWeightedRateTrendSeries = async (
    segmentSlug: StratFinDeskExposureSegmentSlug,
    dealName?: string,
    asOfDate?: string,
  ) => {
    return this.fetchExposureWeightedRateTrendSeries(
      stratFinDeskExposurePath(segmentSlug, 'weighted-rate-trend-series'),
      dealName,
      asOfDate,
    );
  };

  fetchStratFinDeskFacilityConcentrationTop10 = async (
    segmentSlug: StratFinDeskExposureSegmentSlug,
    asOfDate: string,
  ) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'facility-concentration-top10'),
      params: { asOfDate },
    } as never);
    const parsed = normalizeFacilityConcentrationTop10(raw);
    if (!parsed) {
      return { asOfDate, portfolioTotalGrossUsedUsd: 0, top10CombinedSharePct: 0, facilities: [] };
    }
    return parsed;
  };

  fetchStratFinDeskRiskLimitStratification = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'risk-limit-stratification'),
      params: { asOfDate },
    } as never);
    return normalizeRiskLimitStratificationRows(raw, 'plain');
  };

  fetchStratFinDeskConcentrationMetrics = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'concentration-metrics'),
      params: { asOfDate },
    } as never);
    return normalizeConcentrationMetrics(raw, asOfDate);
  };

  fetchStratFinDeskTopCounterparties = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'top-counterparties'),
      params: { asOfDate },
    } as never);
    return normalizeTopCounterpartiesRows(raw);
  };

  fetchStratFinDeskRiskLimitConcentrationSeries = async (
    segmentSlug: StratFinDeskExposureSegmentSlug,
    asOfDate?: string,
  ) => {
    const params = asOfDate ? { asOfDate } : undefined;
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'risk-limit-concentration-series'),
      params,
    } as never);
    return normalizeRiskLimitConcentrationRows(raw, 'plain');
  };

  fetchStratFinDeskQtdTopIncreases = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'qtd-top-increases'),
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchStratFinDeskQtdTopDecreases = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'qtd-top-decreases'),
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchStratFinDeskYtdTopIncreases = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'ytd-top-increases'),
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchStratFinDeskYtdTopDecreases = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'ytd-top-decreases'),
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchStratFinDeskLargeFundingsOver5mm = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'large-fundings-over-5mm'),
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchStratFinDeskLargePaydownsOver5mm = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'large-paydowns-over-5mm'),
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchStratFinDeskInternalRatingMigration = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'internal-rating-migration'),
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingMigrationCells(raw);
  };

  fetchStratFinDeskInternalRatingChanges = async (segmentSlug: StratFinDeskExposureSegmentSlug, asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'internal-rating-changes'),
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingChangeRows(raw);
  };

  fetchStratFinDeskExposureCurrentPositions = async (
    segmentSlug: StratFinDeskExposureSegmentSlug,
    asOfDate: string,
  ) => {
    const raw = await this.get<unknown>({
      path: stratFinDeskExposurePath(segmentSlug, 'current-positions'),
      params: { asOfDate },
    } as never);
    return normalizeExposureCurrentPositionsPayload(raw);
  };

  fetchEmeaExposureAsOfDates = async () => {
    const raw = await this.get<unknown>({ path: EMEA_AS_OF_DATES_PATH } as never);
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return stringArrayFromBody(raw as Record<string, unknown>, 'asOfDates', 'AsOfDates');
    }
    return [];
  };

  fetchEmeaExposureKpis = async (asOfDate: string) => {
    return await this.get<EmeaExposureKpis>({ path: EMEA_KPIS_PATH, params: { asOfDate } } as never);
  };

  fetchEmeaExposureDailyPortfolioBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({ path: EMEA_DAILY_PORTFOLIO_BALANCE_PATH, params } as never);
    return normalizeDailyPortfolioPoints(raw);
  };

  fetchEmeaExposureQuarterlyFundedBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: EMEA_QUARTERLY_FUNDED_BALANCE_PATH,
      params,
    } as never);
    return normalizeEnergyQuarterlyFundedSeriesPayload(raw);
  };

  fetchEmeaExposureFacilityNames = async () => {
    const raw = await this.get<unknown>({ path: EMEA_FACILITIES_PATH } as never);
    if (!raw || typeof raw !== 'object' || raw instanceof Response) {
      return [];
    }
    const body = raw as Record<string, unknown>;
    const listRaw = body.facilities ?? body.Facilities;
    if (!Array.isArray(listRaw)) {
      return [];
    }
    return mapExposureFacilityOptionsFromApiRows(listRaw);
  };

  fetchEmeaFacilityUtilizationSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({ path: EMEA_FACILITY_UTILIZATION_PATH, params } as never);
    return normalizeFacilityUtilizationPoints(raw);
  };

  fetchEmeaWeightedRateTrendSeries = async (dealName?: string, asOfDate?: string) => {
    return this.fetchExposureWeightedRateTrendSeries(EMEA_WEIGHTED_RATE_TREND_PATH, dealName, asOfDate);
  };

  fetchEmeaFacilityConcentrationTop10 = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_FACILITY_CONCENTRATION_TOP10_PATH,
      params: { asOfDate },
    } as never);
    const parsed = normalizeFacilityConcentrationTop10(raw);
    if (!parsed) {
      return { asOfDate, portfolioTotalGrossUsedUsd: 0, top10CombinedSharePct: 0, facilities: [] };
    }
    return parsed;
  };

  fetchEmeaRiskLimitStratification = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_RISK_LIMIT_STRATIFICATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeRiskLimitStratificationRows(raw, 'plain');
  };

  fetchEmeaConcentrationMetrics = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_CONCENTRATION_METRICS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeConcentrationMetrics(raw, asOfDate);
  };

  fetchEmeaTopCounterparties = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_TOP_COUNTERPARTIES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeTopCounterpartiesRows(raw);
  };

  fetchEmeaRiskLimitConcentrationSeries = async (asOfDate?: string) => {
    const params = asOfDate ? { asOfDate } : undefined;
    const raw = await this.get<unknown>({
      path: EMEA_RISK_LIMIT_CONCENTRATION_PATH,
      params,
    } as never);
    return normalizeRiskLimitConcentrationRows(raw, 'plain');
  };

  fetchEmeaQtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_QTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchEmeaQtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_QTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchEmeaYtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_YTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchEmeaYtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_YTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchEmeaLargeFundingsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_LARGE_FUNDINGS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchEmeaLargePaydownsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_LARGE_PAYDOWNS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchEmeaInternalRatingMigration = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_INTERNAL_RATING_MIGRATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingMigrationCells(raw);
  };

  fetchEmeaInternalRatingChanges = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_INTERNAL_RATING_CHANGES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingChangeRows(raw);
  };

  fetchEmeaExposureCurrentPositions = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: EMEA_CURRENT_POSITIONS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeExposureCurrentPositionsPayload(raw);
  };

  fetchMarketplaceExposureAsOfDates = async () => {
    const raw = await this.get<unknown>({ path: MARKETPLACE_AS_OF_DATES_PATH } as never);
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return stringArrayFromBody(raw as Record<string, unknown>, 'asOfDates', 'AsOfDates');
    }
    return [];
  };

  fetchMarketplaceExposureKpis = async (asOfDate: string) => {
    return await this.get<MarketplaceExposureKpis>({
      path: MARKETPLACE_KPIS_PATH,
      params: { asOfDate },
    } as never);
  };

  fetchMarketplaceExposureDailyPortfolioBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: MARKETPLACE_DAILY_PORTFOLIO_BALANCE_PATH,
      params,
    } as never);
    return normalizeDailyPortfolioPoints(raw);
  };

  fetchMarketplaceExposureQuarterlyFundedBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: MARKETPLACE_QUARTERLY_FUNDED_BALANCE_PATH,
      params,
    } as never);
    return normalizeEnergyQuarterlyFundedSeriesPayload(raw);
  };

  fetchMarketplaceExposureFacilityNames = async () => {
    const raw = await this.get<unknown>({ path: MARKETPLACE_FACILITIES_PATH } as never);
    if (!raw || typeof raw !== 'object' || raw instanceof Response) {
      return [];
    }
    const body = raw as Record<string, unknown>;
    const listRaw = body.facilities ?? body.Facilities;
    if (!Array.isArray(listRaw)) {
      return [];
    }
    return mapExposureFacilityOptionsFromApiRows(listRaw);
  };

  fetchMarketplaceFacilityUtilizationSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: MARKETPLACE_FACILITY_UTILIZATION_PATH,
      params,
    } as never);
    return normalizeFacilityUtilizationPoints(raw);
  };

  fetchMarketplaceWeightedRateTrendSeries = async (dealName?: string, asOfDate?: string) => {
    return this.fetchExposureWeightedRateTrendSeries(MARKETPLACE_WEIGHTED_RATE_TREND_PATH, dealName, asOfDate);
  };

  fetchMarketplaceFacilityConcentrationTop10 = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_FACILITY_CONCENTRATION_TOP10_PATH,
      params: { asOfDate },
    } as never);
    const parsed = normalizeFacilityConcentrationTop10(raw);
    if (!parsed) {
      return { asOfDate, portfolioTotalGrossUsedUsd: 0, top10CombinedSharePct: 0, facilities: [] };
    }
    return parsed;
  };

  fetchMarketplaceRiskLimitStratification = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_RISK_LIMIT_STRATIFICATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeRiskLimitStratificationRows(raw, 'plain');
  };

  fetchMarketplaceConcentrationMetrics = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_CONCENTRATION_METRICS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeConcentrationMetrics(raw, asOfDate);
  };

  fetchMarketplaceTopCounterparties = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_TOP_COUNTERPARTIES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeTopCounterpartiesRows(raw);
  };

  fetchMarketplaceRiskLimitConcentrationSeries = async (asOfDate?: string) => {
    const params = asOfDate ? { asOfDate } : undefined;
    const raw = await this.get<unknown>({
      path: MARKETPLACE_RISK_LIMIT_CONCENTRATION_PATH,
      params,
    } as never);
    return normalizeRiskLimitConcentrationRows(raw, 'plain');
  };

  fetchMarketplaceQtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_QTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchMarketplaceQtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_QTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchMarketplaceYtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_YTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchMarketplaceYtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_YTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchMarketplaceLargeFundingsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_LARGE_FUNDINGS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchMarketplaceLargePaydownsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_LARGE_PAYDOWNS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchMarketplaceInternalRatingMigration = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_INTERNAL_RATING_MIGRATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingMigrationCells(raw);
  };

  fetchMarketplaceInternalRatingChanges = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_INTERNAL_RATING_CHANGES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingChangeRows(raw);
  };

  fetchMarketplaceExposureCurrentPositions = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: MARKETPLACE_CURRENT_POSITIONS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeExposureCurrentPositionsPayload(raw);
  };

  fetchFundFinanceExposureAsOfDates = async () => {
    const raw = await this.get<unknown>({ path: FUND_FINANCE_AS_OF_DATES_PATH } as never);
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return stringArrayFromBody(raw as Record<string, unknown>, 'asOfDates', 'AsOfDates');
    }
    return [];
  };

  fetchFundFinanceExposureKpis = async (asOfDate: string) => {
    return await this.get<FundFinanceExposureKpis>({
      path: FUND_FINANCE_KPIS_PATH,
      params: { asOfDate },
    } as never);
  };

  fetchFundFinanceExposureDailyPortfolioBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_DAILY_PORTFOLIO_BALANCE_PATH,
      params,
    } as never);
    return normalizeDailyPortfolioPoints(raw);
  };

  fetchFundFinanceExposureQuarterlyFundedBalanceSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_QUARTERLY_FUNDED_BALANCE_PATH,
      params,
    } as never);
    return normalizeEnergyQuarterlyFundedSeriesPayload(raw);
  };

  fetchFundFinanceExposureFacilityNames = async () => {
    const raw = await this.get<unknown>({ path: FUND_FINANCE_FACILITIES_PATH } as never);
    if (!raw || typeof raw !== 'object' || raw instanceof Response) {
      return [];
    }
    const body = raw as Record<string, unknown>;
    const listRaw = body.facilities ?? body.Facilities;
    if (!Array.isArray(listRaw)) {
      return [];
    }
    return mapExposureFacilityOptionsFromApiRows(listRaw);
  };

  fetchFundFinanceFacilityUtilizationSeries = async (dealName?: string, asOfDate?: string) => {
    const params = { ...(dealName ? { dealName } : {}), ...(asOfDate ? { asOfDate } : {}) };
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_FACILITY_UTILIZATION_PATH,
      params,
    } as never);
    return normalizeFacilityUtilizationPoints(raw);
  };

  fetchFundFinanceWeightedRateTrendSeries = async (dealName?: string, asOfDate?: string) => {
    return this.fetchExposureWeightedRateTrendSeries(FUND_FINANCE_WEIGHTED_RATE_TREND_PATH, dealName, asOfDate);
  };

  fetchFundFinanceFacilityConcentrationTop10 = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_FACILITY_CONCENTRATION_TOP10_PATH,
      params: { asOfDate },
    } as never);
    const parsed = normalizeFacilityConcentrationTop10(raw);
    if (!parsed) {
      return { asOfDate, portfolioTotalGrossUsedUsd: 0, top10CombinedSharePct: 0, facilities: [] };
    }
    return parsed;
  };

  fetchFundFinanceRiskLimitStratification = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_RISK_LIMIT_STRATIFICATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeRiskLimitStratificationRows(raw, 'plain');
  };

  fetchFundFinanceConcentrationMetrics = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_CONCENTRATION_METRICS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeConcentrationMetrics(raw, asOfDate);
  };

  fetchFundFinanceTopCounterparties = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_TOP_COUNTERPARTIES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeTopCounterpartiesRows(raw);
  };

  fetchFundFinanceRiskLimitConcentrationSeries = async (asOfDate?: string) => {
    const params = asOfDate ? { asOfDate } : undefined;
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_RISK_LIMIT_CONCENTRATION_PATH,
      params,
    } as never);
    return normalizeRiskLimitConcentrationRows(raw, 'plain');
  };

  fetchFundFinanceQtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_QTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchFundFinanceQtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_QTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchFundFinanceYtdTopIncreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_YTD_TOP_INCREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchFundFinanceYtdTopDecreases = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_YTD_TOP_DECREASES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyDealMoversPayload(raw, 'plain');
  };

  fetchFundFinanceLargeFundingsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_LARGE_FUNDINGS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchFundFinanceLargePaydownsOver5mm = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_LARGE_PAYDOWNS_OVER_5MM_PATH,
      params: { asOfDate },
    } as never);
    return normalizeLargeMovementRows(raw);
  };

  fetchFundFinanceInternalRatingMigration = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_INTERNAL_RATING_MIGRATION_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingMigrationCells(raw);
  };

  fetchFundFinanceInternalRatingChanges = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_INTERNAL_RATING_CHANGES_PATH,
      params: { asOfDate },
    } as never);
    return normalizeEnergyInternalRatingChangeRows(raw);
  };

  fetchFundFinanceExposureCurrentPositions = async (asOfDate: string) => {
    const raw = await this.get<unknown>({
      path: FUND_FINANCE_CURRENT_POSITIONS_PATH,
      params: { asOfDate },
    } as never);
    return normalizeExposureCurrentPositionsPayload(raw);
  };
}
